<script setup lang="ts">
import type { GameVehicleSummary, WikiItem, WikiVehiclePort } from "~~/types/gamedata";

definePageMeta({ layout: "shipexkurs" });

const route = useRoute();
const id = computed(() => route.params.id as string);
const nuxtApp = useNuxtApp();

// Nur der schlanke Index ist für den Seitenwechsel zwingend. Er liegt beim
// Wechsel aus Katalog oder Command Palette bereits im Client-Cache. Die sehr
// großen Wiki-Vollantworten laden bewusst lazy, damit ein langsamer Upstream
// die Vue-Router-Navigation nicht mehr im Page-Suspense festhält.
const {
  data: vehicleIndex,
  pending: vehicleIndexPending,
  error: vehicleIndexError,
  refresh: refreshVehicleIndex,
} = useVehicleIndex({ lazy: true });
const {
  data: raw,
  pending: rawPending,
  error: rawError,
  refresh: refreshRaw,
} = useVehicle(id, { include: 'ports', lazy: true, server: false });

// `include=ports` liefert Beschreibung, Galerie, Kennzahlen und Ports in einer
// Antwort. Zuvor liefen dafür zwei nahezu identische große Requests parallel.
const ports = computed<WikiVehiclePort[]>(() => raw.value?.ports ?? []);
const portsPending = rawPending;
const portsError = rawError;

const MAX_AUTOMATIC_DETAIL_RETRIES = 3;
const DETAIL_RETRY_DELAYS_MS = [1_500, 3_500, 7_500] as const;
const rawSlow = ref(false);
const rawRetryAttempt = ref(0);
const rawRetryScheduled = ref(false);
let rawSlowTimer: ReturnType<typeof setTimeout> | null = null;
let rawRetryTimer: ReturnType<typeof setTimeout> | null = null;

function clearRawSlowTimer() {
  if (rawSlowTimer) clearTimeout(rawSlowTimer);
  rawSlowTimer = null;
}

function clearRawRetryTimer() {
  if (rawRetryTimer) clearTimeout(rawRetryTimer);
  rawRetryTimer = null;
  rawRetryScheduled.value = false;
}

function scheduleRawRetry() {
  if (rawRetryAttempt.value >= MAX_AUTOMATIC_DETAIL_RETRIES || rawRetryTimer) return;

  const delay = DETAIL_RETRY_DELAYS_MS[rawRetryAttempt.value] ?? DETAIL_RETRY_DELAYS_MS.at(-1)!;
  rawRetryAttempt.value += 1;
  rawRetryScheduled.value = true;
  rawRetryTimer = setTimeout(() => {
    rawRetryTimer = null;
    rawRetryScheduled.value = false;
    void refreshRaw();
  }, delay);
}

async function retryRawNow() {
  clearRawRetryTimer();
  rawRetryAttempt.value = 0;
  await refreshRaw();
}

watch(
  [id, rawPending],
  ([, pending]) => {
    clearRawSlowTimer();
    rawSlow.value = false;
    if (pending) {
      rawSlowTimer = setTimeout(() => {
        if (rawPending.value) rawSlow.value = true;
      }, 30_000);
    }
  },
  { immediate: true },
);

watch(rawError, (error) => {
  if (error) scheduleRawRetry();
  else if (!rawPending.value) rawRetryAttempt.value = 0;
});

watch(id, () => {
  clearRawRetryTimer();
  rawRetryAttempt.value = 0;
});

onBeforeUnmount(() => {
  clearRawSlowTimer();
  clearRawRetryTimer();
});

// Beschreibung und Galerie stammen aus derselben Vollantwort wie die
// technischen Werte. Zuvor wurde das identische Dokument doppelt geladen.
const detail = computed(() => toVehicleDetail(raw.value));

const summaryFromIndex = computed(() => vehicleIndex.value.find((vehicle) => vehicle.id === id.value) ?? null);

// Lackierungs-/Sondereditionen-Varianten sind bewusst nicht im Hauptindex
// (siehe `BLACKLISTED_SHIP_SLUGS`), sollen aber trotzdem über einen direkten
// Link (von der "Lackierungen"-Sektion ihres Basisschiffs) eine eigene Seite
// haben — deshalb hier nur bei fehlendem Index-Treffer nachgeladen.
// Kaufbare Paint-Items hängen dagegen an `raw.class_name`, der erst nach dem
// obigen Promise.all feststeht.
const fallbackSummaryId = computed(() => (
  vehicleIndexPending.value || summaryFromIndex.value ? null : id.value
));
const {
  data: fallbackSummary,
  pending: fallbackSummaryPending,
  error: fallbackSummaryError,
  refresh: refreshFallbackSummary,
} = useVehicleFallbackSummary(
  fallbackSummaryId,
  { lazy: true },
);
const { data: paints, pending: paintsPending } = useVehiclePaints(
  computed(() => raw.value?.class_name),
  computed(() => raw.value?.port_tags),
  { lazy: true, server: false },
);

const summary = computed(() => summaryFromIndex.value ?? fallbackSummary.value);

const summaryPending = computed(() =>
  vehicleIndexPending.value
  || (!!fallbackSummaryId.value && fallbackSummaryPending.value),
);

async function retrySummary() {
  await refreshVehicleIndex();
  if (!summaryFromIndex.value) await refreshFallbackSummary();
}

// Ersatzflieger für Konzeptschiffe (z.B. "Idris-P" für die "Javelin") —
// `summary.loaners` trägt nur schlanke Referenzen, hier gegen den bereits
// geladenen Fahrzeugindex zu vollen Karten aufgelöst. Fehlt ein Loaner im
// Index (z.B. selbst eine ausgeblendete Sonderedition), wird er stillschweigend
// übersprungen statt eine kaputte Karte zu zeigen.
const loaners = computed(() =>
  (summary.value?.loaners ?? [])
    .map((loaner) => vehicleIndex.value.find((vehicle) => vehicle.id === loaner.id))
    .filter((vehicle): vehicle is GameVehicleSummary => !!vehicle),
);

const detailTabs = computed(() => [
  {
    label: 'Komponenten',
    value: 'components',
    slot: 'components',
    icon: 'i-lucide-cpu',
  },
  {
    label: `Lackierungen (${paints.value.length})`,
    value: 'paints',
    slot: 'paints',
    icon: 'i-lucide-paintbrush',
  },
  {
    label: `Loaner (${loaners.value.length})`,
    value: 'loaners',
    slot: 'loaners',
    icon: 'i-lucide-repeat-2',
  },
]);

// Für generische Komponenten ohne eigenen Produktnamen (z.B. Frachtraster,
// "Orig 100i Cargogrid" statt nur "Cargo Grid").
const shipLabel = computed(() => {
  const manufacturer = summary.value?.manufacturer;
  const code = manufacturer?.code || manufacturer?.short || "";
  const titledCode = code ? code.charAt(0).toUpperCase() + code.slice(1).toLowerCase() : "";
  return [titledCode, summary.value?.name].filter(Boolean).join(" ");
});

// Ports kennen nur Namen der verbauten Teile — die Kennzahlen (DPS, Schild-HP,
// Kühlrate, ...) für den Komponentenbaum stecken erst in den Item-Volldaten.
// `resolvePortItems()` (app/utils/resolve-port-items.ts) sammelt dafür
// rekursiv auch verschachtelte Vehicle-Ports (z.B. Raketen unter einem
// Waffenhalter) mit ein — wiederverwendet für das Basisschiff (unten) und für
// die Loadout-Varianten (siehe `variantLoadout` unten) sowie den Schiffsvergleich.
const componentItems = shallowRef<Map<string, WikiItem>>(new Map());
const componentItemsPending = ref(false);
const componentItemsError = ref<unknown>(null);
let componentLoadVersion = 0;

// Item-Volldaten können bei einem kalten Cache viele Upstream-Aufrufe
// verursachen. Sie reichern den bereits in den Ports enthaltenen
// Komponentenbaum nachträglich an und dürfen den Seitenwechsel nicht sperren.
watch(
  [id, ports],
  async ([, currentPorts]) => {
    const loadVersion = ++componentLoadVersion;
    componentItems.value = new Map();
    componentItemsError.value = null;

    if (!currentPorts.length) {
      componentItemsPending.value = false;
      return;
    }

    componentItemsPending.value = true;
    try {
      const loadedItems = await resolvePortItems(currentPorts);
      if (loadVersion === componentLoadVersion) componentItems.value = loadedItems;
    } catch (error) {
      if (loadVersion === componentLoadVersion) componentItemsError.value = error;
    } finally {
      if (loadVersion === componentLoadVersion) componentItemsPending.value = false;
    }
  },
  { immediate: true },
);

// Loadout-Varianten (Pirate/Pyam/Wikelo) desselben Schiffs — unterscheiden
// sich nur im Komponenten-Loadout (siehe `summary.variants`), deshalb bleibt
// der Rest der Seite (Hero, Stats, Preis, ...) beim Basisschiff und nur der
// Komponentenbaum wird bei Auswahl ausgetauscht.
//
// Der API-`name` ist bereits die echte In-Game-Bezeichnung (z.B. "Sabre
// Firebird Wikelo War Special", "Caterpillar Pirate") — eigene Edition-Labels
// draufzusetzen (z.B. "(Wikelo)") war doppelt gemoppelt und teils sachlich
// falsch (Exec-Editions heißen nicht "Military"/"Stealth", sondern tragen
// beide denselben Namen "… PYAM Exec"). Der Slug-Suffix (`subType`) wird
// deshalb nur angehängt, wenn zwei Varianten sonst identisch benannt wären.
const selectedVariantId = ref(id.value);
watch(id, (value) => { selectedVariantId.value = value; });

const variantSelectItems = computed(() => {
  if (!summary.value) return [];

  const nameCounts = new Map<string, number>();
  for (const variant of summary.value.variants) {
    nameCounts.set(variant.name, (nameCounts.get(variant.name) ?? 0) + 1);
  }

  return [
    { label: `${summary.value.name} (Standard)`, value: summary.value.id },
    ...summary.value.variants.map((variant) => {
      const needsDisambiguation = (nameCounts.get(variant.name) ?? 0) > 1 && variant.subType;
      const label = needsDisambiguation ? `${variant.name} (${variant.subType})` : variant.name;
      return { label, value: variant.id };
    }),
  ];
});

// `useAsyncData` statt eines manuellen `watch(...)` — Composables wie
// `fetchAllVehiclePorts`/`fetchItemsByUuid` brauchen Zugriff auf die
// Nuxt-Instanz, der in einem eigenhändigen async `watch`-Callback nach einem
// `await` verloren geht ("called outside of a plugin, Nuxt hook, ... Vue
// setup function"). `useAsyncData` kümmert sich intern korrekt darum.
const { data: variantLoadout, pending: variantLoading } = useLazyAsyncData(
  () => `shipexkurs:variant-loadout:${selectedVariantId.value}`,
  async () => {
    if (selectedVariantId.value === id.value) {
      return { ports: ports.value, items: componentItems.value };
    }
    const loadedPorts = await nuxtApp.runWithContext(() => fetchAllVehiclePorts(selectedVariantId.value));
    const loadedItems = await resolvePortItems(loadedPorts);
    return { ports: loadedPorts, items: loadedItems };
  },
  { watch: [selectedVariantId], default: () => null, server: false },
);

const displayingBaseLoadout = computed(() => selectedVariantId.value === id.value);
const displayedPorts = computed(() =>
  displayingBaseLoadout.value ? ports.value : (variantLoadout.value?.ports ?? []),
);
const displayedItems = computed(() =>
  displayingBaseLoadout.value ? componentItems.value : (variantLoadout.value?.items ?? new Map()),
);
const loadoutPending = computed(() =>
  displayingBaseLoadout.value
    ? portsPending.value
    : variantLoading.value,
);

const updatedAtFormatted = computed(() => {
  if (!raw.value?.updated_at) return null;
  return new Intl.DateTimeFormat("de-DE", { dateStyle: "medium" }).format(new Date(raw.value.updated_at));
});

const runtimeConfig = useRuntimeConfig();

useSeoMeta({
  title: () => (summary.value ? `${summary.value.name} | ShipExkurs` : "Schiff | ShipExkurs"),
  description: () =>
    detail.value?.description?.slice(0, 160)
    ?? `${summary.value?.name ?? "Dieses Schiff"} von ${summary.value?.manufacturer?.name ?? "unbekanntem Hersteller"} – technische Daten auf ShipExkurs.`,
  ogTitle: () => summary.value?.name,
  ogImage: () => summary.value?.image?.url,
});

useHead({
  link: [{ rel: "canonical", href: `${runtimeConfig.public.SITE_URL}/shipexkurs/ships/${id.value}` }],
});
</script>

<template>
  <div v-if="summary" class="px-4 py-8 sm:px-6 lg:px-8">
    <nav class="mb-4 flex items-center gap-1.5 text-sm text-neutral-500">
      <NuxtLink to="/shipexkurs" class="hover:text-primary">Startseite</NuxtLink>
      <UIcon name="i-lucide-chevron-right" class="size-3.5" />
      <NuxtLink to="/shipexkurs" class="hover:text-primary">Schiffe</NuxtLink>
      <UIcon name="i-lucide-chevron-right" class="size-3.5" />
      <NuxtLink :to="`/shipexkurs/ships/${id}`" class="hover:text-primary">{{ summary.name }}</NuxtLink>
    </nav>

    <ShipexkursShipHero
      :vehicle="summary"
      :detail="detail"
      :raw="raw"
      :loading="rawPending"
    />

    <div
      v-if="(rawPending && rawSlow) || (rawError && rawRetryScheduled)"
      class="mt-4"
      aria-live="polite"
    >
      <UAlert
        color="info"
        icon="i-lucide-loader-2"
        title="Der Abruf dauert länger als gewöhnlich"
        :description="rawRetryScheduled
          ? `Automatischer Versuch ${rawRetryAttempt} von ${MAX_AUTOMATIC_DETAIL_RETRIES} startet gleich. Die Basisdaten bleiben sichtbar.`
          : 'Die Anfrage läuft weiter. Beschreibung, Kennzahlen und Komponenten erscheinen automatisch, sobald die Antwort eintrifft.'"
        variant="subtle"
      />
    </div>

    <div v-else-if="rawError" class="mt-4 flex flex-wrap items-center gap-3">
      <UAlert
        class="min-w-0 flex-1"
        color="warning"
        icon="i-lucide-cloud-off"
        title="Technische Detaildaten sind momentan nicht erreichbar"
        description="Mehrere automatische Abrufe waren nicht erfolgreich. Die bereits geladenen Basisdaten bleiben verfügbar."
        variant="subtle"
      />
      <UButton
        color="neutral"
        icon="i-lucide-refresh-cw"
        label="Erneut versuchen"
        variant="outline"
        @click="retryRawNow"
      />
    </div>

    <!-- <div class="grid grid-cols-1 gap-8 lg:grid-cols-3">
      <div class="lg:col-span-2">
        <h1 class="!my-0 mb-3 text-3xl font-bold text-white">{{ summary.name }}</h1>
        <ShipexkursShipGallery :images="detail?.images ?? []" :fallback-vehicle="summary" />
      </div>

      <ShipexkursHeroSidebar :vehicle="summary" :raw="raw" /> -->
    <!-- </div> -->

    <div v-if="raw" class="mt-8">
      <ShipexkursDefenseCard :raw="raw" />
    </div>

    <UCard
      variant="outline"
      class="mt-8 border border-default bg-bunker-950 ring-0"
      :ui="{ body: 'px-0 py-2 sm:px-0 sm:py-2' }"
    >
      <UTabs
        default-value="components"
        :items="detailTabs"
        :ui="{ root: 'items-stretch', list: 'mx-2' }"
      >
        <template #components>
          <div class="px-4 pb-4 sm:px-6">
            <div v-if="summary.variants.length" class="mb-4 flex items-center gap-3">
              <span class="text-sm text-neutral-400">Variante</span>
              <USelectMenu
                v-model="selectedVariantId"
                :items="variantSelectItems"
                value-key="value"
                label-key="label"
                size="xl"
                class="w-full max-w-md"
              />
            </div>
            <div v-if="portsError && !rawRetryScheduled" class="mb-4 flex flex-wrap items-center gap-3">
              <UAlert
                class="min-w-0 flex-1"
                color="warning"
                icon="i-lucide-cloud-off"
                title="Ausrüstungsdaten sind momentan nicht erreichbar"
                description="Die technischen Schiffsdaten konnten auch nach mehreren automatischen Versuchen nicht geladen werden."
                variant="subtle"
              />
              <UButton
                color="neutral"
                icon="i-lucide-refresh-cw"
                label="Erneut versuchen"
                variant="outline"
                @click="retryRawNow"
              />
            </div>
            <p
              v-else-if="componentItemsPending"
              class="mb-3 flex items-center gap-2 text-xs text-neutral-500"
            >
              <UIcon name="i-lucide-loader-2" class="size-3.5 animate-spin" />
              Zusätzliche Komponentenwerte werden im Hintergrund ergänzt…
            </p>
            <p
              v-else-if="componentItemsError"
              class="mb-3 text-xs text-neutral-500"
            >
              Einige zusätzliche Komponentenwerte konnten nicht geladen werden.
            </p>
            <ShipexkursComponentTree
              v-if="!portsError"
              :ports="displayedPorts"
              :items="displayedItems"
              :ship-label="shipLabel"
              :loading="loadoutPending"
            />
          </div>
        </template>

        <template #paints>
          <div class="px-4 pb-4 sm:px-6">
            <div v-if="paintsPending" class="flex items-center justify-center gap-2 py-8 text-sm text-neutral-500">
              <UIcon name="i-lucide-loader-2" class="size-4 animate-spin" />
              Lade Lackierungen…
            </div>
            <div v-else-if="paints.length" class="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-6 lg:grid-cols-4 xl:grid-cols-5">
              <ShipexkursPaintCard v-for="paint in paints" :key="paint.uuid" :item="paint" />
            </div>
            <p v-else class="!my-0 py-8 text-center text-sm text-neutral-500">
              Für dieses Fahrzeug sind keine Lackierungen hinterlegt.
            </p>
          </div>
        </template>

        <template #loaners>
          <div class="px-4 pb-4 sm:px-6">
            <template v-if="loaners.length">
              <p class="mb-3 text-xs text-neutral-500">
                Solange die {{ summary.name }} noch nicht flugbereit ist, kannst du stattdessen diese Schiffe fliegen.
              </p>
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 xl:grid-cols-4">
                <ShipexkursShipCard v-for="loaner in loaners" :key="loaner.id" :vehicle="loaner" />
              </div>
            </template>
            <p v-else class="!my-0 py-8 text-center text-sm text-neutral-500">
              Für dieses Fahrzeug sind keine Loaner hinterlegt.
            </p>
          </div>
        </template>
      </UTabs>
    </UCard>

    <p v-if="raw?.version || updatedAtFormatted" class="mt-10 text-xs text-neutral-600">
      <span v-if="raw?.version">Datenversion {{ raw.version }}</span>
      <span v-if="raw?.version && updatedAtFormatted"> · </span>
      <span v-if="updatedAtFormatted">Stand {{ updatedAtFormatted }}</span>
    </p>
  </div>

  <div v-else-if="summaryPending" class="px-4 py-8 sm:px-6 lg:px-8" aria-live="polite">
    <div class="mb-4 flex items-center gap-2">
      <USkeleton class="h-4 w-20" />
      <UIcon name="i-lucide-chevron-right" class="size-3.5 text-neutral-700" />
      <USkeleton class="h-4 w-24" />
    </div>
    <div class="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(18rem,1fr)]">
      <div class="space-y-4">
        <div class="flex items-center justify-between gap-6">
          <USkeleton class="h-9 w-72 max-w-[65%]" />
          <USkeleton class="h-6 w-28" />
        </div>
        <USkeleton class="aspect-video w-full rounded-lg" />
        <div class="space-y-2 border-l-2 border-l-primary/30 pl-4">
          <USkeleton class="h-3 w-full" />
          <USkeleton class="h-3 w-5/6" />
          <USkeleton class="h-3 w-2/3" />
        </div>
      </div>
      <div class="space-y-4">
        <USkeleton class="h-36 w-full rounded-lg" />
        <USkeleton v-for="index in 3" :key="index" class="h-28 w-full rounded-lg" />
      </div>
    </div>
    <p class="mt-5 flex items-center gap-2 text-sm text-neutral-400">
      <UIcon name="i-lucide-loader-2" class="size-4 animate-spin text-primary" />
      Schiffsdaten werden vorbereitet…
    </p>
  </div>

  <div v-else class="px-4 py-16 sm:px-6 lg:px-8">
    <UAlert
      :color="vehicleIndexError || fallbackSummaryError ? 'warning' : 'error'"
      :icon="vehicleIndexError || fallbackSummaryError ? 'i-lucide-cloud-off' : 'i-lucide-circle-x'"
      :title="vehicleIndexError || fallbackSummaryError ? 'Schiffsdaten konnten nicht geladen werden' : 'Schiff nicht gefunden'"
      :description="vehicleIndexError || fallbackSummaryError
        ? 'Die Verbindung zur Schiffsdatenbank ist fehlgeschlagen.'
        : 'Für diese Adresse ist kein Fahrzeug hinterlegt.'"
      variant="subtle"
      :actions="[{
        label: 'Erneut versuchen',
        icon: 'i-lucide-refresh-cw',
        color: 'neutral',
        variant: 'outline',
        onClick: retrySummary,
      }]"
    />
  </div>
</template>
